import { useQuery } from "@tanstack/react-query";

export const SUPPORTED_CURRENCIES = {
  GHS: "Ghanaian Cedi",
  USD: "US Dollar",
  EUR: "Euro",
  NGN: "Nigerian Naira",
  KES: "Kenyan Shilling",
  ZAR: "South African Rand",
  UGX: "Ugandan Shilling",
  TZS: "Tanzanian Shilling",
  RWF: "Rwandan Franc",
  XOF: "West African CFA",
  XAF: "Central African CFA",
} as const;

export type CurrencyCode = keyof typeof SUPPORTED_CURRENCIES;

export const useExchangeRate = (from: CurrencyCode, to: CurrencyCode) => {
  return useQuery({
    queryKey: ['exchangeRate', from, to],
    queryFn: async () => {
      try {
        const response = await fetch(
          `https://v6.exchangerate-api.com/v6/${process.env.EXCHANGE_RATE_API_KEY}/pair/${from}/${to}`
        );
        const data = await response.json();
        return data.conversion_rate;
      } catch (error) {
        console.error('Error fetching exchange rate:', error);
        // Fallback to mock rates if API fails
        const mockRates: Record<string, number> = {
          'GHS-USD': 0.083,
          'USD-GHS': 12.05,
          'EUR-GHS': 13.10,
          'GHS-EUR': 0.076,
          'NGN-GHS': 0.037,
          'GHS-NGN': 27.03,
          'KES-GHS': 0.067,
          'GHS-KES': 14.93,
          'ZAR-GHS': 0.64,
          'GHS-ZAR': 1.56,
          'UGX-GHS': 0.0022,
          'GHS-UGX': 454.55,
          'TZS-GHS': 0.0033,
          'GHS-TZS': 303.03,
          'RWF-GHS': 0.0069,
          'GHS-RWF': 144.93,
          'XOF-GHS': 0.014,
          'GHS-XOF': 71.43,
          'XAF-GHS': 0.014,
          'GHS-XAF': 71.43,
        };
        
        return mockRates[`${from}-${to}`] || 1;
      }
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

export const formatCurrency = (amount: number, currency: CurrencyCode): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
};